import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Pressable } from '@atlaskit/primitives/compiled';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { FastshiftCsProfilerLinkedTickets } from './fastshift-cs-profiler-linked-tickets';
import { useFastshiftCsProfilerAnalytics } from './hooks/use-fastshift-cs-profiler-analytics';
import { messages } from './messages';

import type {
	FastshiftCsProfilerCustomerProfile,
	FastshiftCsProfilerCustomerProfileTheme,
	FastshiftCsProfilerInsight,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export type FastshiftCsProfilerThemeDisplayProps = {
	customerProfile: FastshiftCsProfilerCustomerProfile;
	isExportingToConfluence?: boolean;
	onExportToConfluence?: () => void;
};

const styles = cssMap({
	resultsPanel: {
		display: 'block',
		paddingBlockStart: token('space.200'),
	},
	exportButtonWrapper: {
		paddingBlockEnd: token('space.050'),
	},
	resultProfileChip: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.full'),
		display: 'inline-flex',
		gap: token('space.075'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
	},
	themeSectionsWrapper: {
		paddingBlockStart: token('space.100'),
	},
	themeRow: {
		backgroundColor: token('elevation.surface'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		overflow: 'hidden',
	},
	themeDetails: {
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	insightsWrapper: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockEnd: token('space.200'),
	},
	detailSectionHeading: {
		color: token('color.text.subtlest'),
	},
	recommendationItem: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
	},
	recommendationsList: {
		display: 'grid',
		gap: token('space.050'),
		marginBlockEnd: 0,
		marginBlockStart: 0,
		paddingInlineStart: token('space.300'),
	},
	recommendationsWrapper: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockEnd: token('space.200'),
	},
	resultsHeadingRow: {
		width: '100%',
	},
	resultsHeadingDivider: {
		border: '0 solid transparent',
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		marginBlockEnd: 0,
		marginBlockStart: 0,
		width: '100%',
	},
	themeRowHeader: {
		alignItems: 'center',
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
		color: token('color.text'),
		display: 'flex',
		minHeight: '64px',
		paddingBlockEnd: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingInline: token('space.200'),
		position: 'relative',
		textAlign: 'start',
		width: '100%',
	},
	themeRowHeaderExpanded: {
		alignItems: 'center',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderInlineStartColor: token('color.border.selected'),
		borderInlineStartStyle: 'solid',
		borderInlineStartWidth: token('border.width.selected'),
		boxSizing: 'border-box',
		color: token('color.text'),
		display: 'flex',
		minHeight: '64px',
		paddingBlockEnd: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingInline: token('space.200'),
		position: 'relative',
		textAlign: 'start',
		width: '100%',
	},
	themeRowToggleButton: {
		backgroundColor: 'transparent',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: 0,
		cursor: 'pointer',
		insetBlockEnd: 0,
		insetBlockStart: 0,
		insetInlineEnd: 0,
		insetInlineStart: 0,
		paddingBlock: 0,
		paddingInline: 0,
		position: 'absolute',
		width: '100%',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	themeRowToggleButtonExpanded: {
		backgroundColor: 'transparent',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: 0,
		cursor: 'pointer',
		insetBlockEnd: 0,
		insetBlockStart: 0,
		insetInlineEnd: 0,
		insetInlineStart: 0,
		paddingBlock: 0,
		paddingInline: 0,
		position: 'absolute',
		width: '100%',
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtlest.pressed'),
		},
	},
	themeRowHeaderContent: {
		pointerEvents: 'none',
		position: 'relative',
		width: '100%',
	},
	themeInfoButtonWrapper: {
		pointerEvents: 'auto',
	},
});

const themeStatusIconConfigsByTier = {
	HIGH: {
		icon: StatusErrorIcon,
		appearance: 'red',
		testId: 'cs-profiler-theme-icon-hard-blocker',
	},
	BASE: {
		icon: StatusWarningIcon,
		appearance: 'orange',
		testId: 'cs-profiler-theme-icon-blocker',
	},
	WATCH: {
		icon: StatusInformationIcon,
		appearance: 'gray',
		testId: 'cs-profiler-theme-icon-watch',
	},
} as const;

const ThemeStatusIcon = ({ theme }: { theme: FastshiftCsProfilerCustomerProfileTheme }) => {
	const config = themeStatusIconConfigsByTier[theme.tier];

	return (
		<IconTile
			size="medium"
			label=""
			icon={config.icon}
			appearance={config.appearance}
			testId={config.testId}
		/>
	);
};

const themeTierSortRank = {
	HIGH: 0,
	BASE: 1,
	WATCH: 2,
} satisfies Record<FastshiftCsProfilerCustomerProfileTheme['tier'], number>;

const insightMessagesByDimension: Record<
	FastshiftCsProfilerInsight['dimension'],
	typeof messages.industryInsight
> = {
	REGION_INDUSTRY: messages.regionIndustryInsight,
	INDUSTRY: messages.industryInsight,
	REGION_SEGMENT: messages.regionSegmentInsight,
	SEGMENT: messages.segmentInsight,
};

const getFormattedPercentage = (value: number) => Math.round(value * 100);

const bold = (chunks: ReactNode) => <strong>{chunks}</strong>;

const sortThemesBySeverity = (themes: FastshiftCsProfilerCustomerProfileTheme[]) =>
	themes.slice().sort((firstTheme, secondTheme) => {
		return themeTierSortRank[firstTheme.tier] - themeTierSortRank[secondTheme.tier];
	});

const ThemeDetails = ({
	theme,
	customerProfile,
	intl,
}: {
	theme: FastshiftCsProfilerCustomerProfileTheme;
	customerProfile: FastshiftCsProfilerCustomerProfile;
	intl: IntlShape;
}) => {
	const { description, mitigations } = theme.themeDetails;
	const { formatMessage } = intl;
	const recommendations = mitigations ?? [];
	const themeName = theme.themeDetails.name;

	return (
		<Box xcss={styles.themeDetails}>
			<Stack space="space.150">
				<Stack space="space.075" xcss={styles.insightsWrapper}>
					<Heading size="xxsmall" as="h4">
						<Box as="span" xcss={styles.detailSectionHeading}>
							{formatMessage(messages.insightsHeading)}
						</Box>
					</Heading>
					{theme.insights.length > 0 ? (
						<Stack space="space.050">
							{theme.insights.map((insight) => (
								<Text key={`${insight.dimension}-${insight.cellRate}-${insight.hits}`}>
									<FormattedMessage
										{...insightMessagesByDimension[insight.dimension]}
										values={{
											region: customerProfile.selection.region,
											industry: customerProfile.selection.industry,
											segment: customerProfile.selection.segment,
											percentage: getFormattedPercentage(insight.cellRate),
											bold,
										}}
									/>
								</Text>
							))}
						</Stack>
					) : theme.tier === 'BASE' ? (
						<Text>
							<FormattedMessage
								{...messages.baseRateInsight}
								values={{
									percentage: getFormattedPercentage(theme.baseRate),
									bold,
								}}
							/>
						</Text>
					) : (
						<Text color="color.text.subtle">
							{description ?? formatMessage(messages.noInsights)}
						</Text>
					)}
				</Stack>

				{recommendations.length > 0 && (
					<Stack space="space.075" xcss={styles.recommendationsWrapper}>
						<Heading size="xxsmall" as="h4">
							<Box as="span" xcss={styles.detailSectionHeading}>
								{formatMessage(messages.recommendationsHeading)}
							</Box>
						</Heading>
						<Box as="ul" xcss={styles.recommendationsList}>
							{recommendations.map((mitigation) => (
								<Box as="li" key={mitigation} xcss={styles.recommendationItem}>
									<Text>{mitigation}</Text>
								</Box>
							))}
						</Box>
					</Stack>
				)}

				<FastshiftCsProfilerLinkedTickets themeName={themeName} />
			</Stack>
		</Box>
	);
};

const ThemeRow = ({
	theme,
	isExpanded,
	onToggle,
	customerProfile,
	intl,
}: {
	theme: FastshiftCsProfilerCustomerProfileTheme;
	isExpanded: boolean;
	onToggle: () => void;
	customerProfile: FastshiftCsProfilerCustomerProfile;
	intl: IntlShape;
}) => {
	const themeName = theme.themeDetails.name;
	const { formatMessage } = intl;
	const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

	return (
		<Box xcss={styles.themeRow} testId={`cs-profiler-theme-${themeName}`}>
			<Box xcss={isExpanded ? styles.themeRowHeaderExpanded : styles.themeRowHeader}>
				{/* Preserve full-row toggling without nesting the info IconButton inside a button. */}
				<Pressable
					type="button"
					xcss={isExpanded ? styles.themeRowToggleButtonExpanded : styles.themeRowToggleButton}
					onClick={onToggle}
					aria-expanded={isExpanded}
					aria-label={formatMessage(
						isExpanded ? messages.collapseThemeLabel : messages.expandThemeLabel,
						{
							themeName,
						},
					)}
				/>
				<Box xcss={styles.themeRowHeaderContent}>
					<Inline alignBlock="center" spread="space-between" space="space.150">
						<Inline alignBlock="center" space="space.150">
							<ThemeStatusIcon theme={theme} />
							<Inline alignBlock="center" space="space.075">
								<Text weight="semibold">{themeName}</Text>
								<Tooltip
									content={theme.themeDetails.description ?? formatMessage(messages.themeInfoLabel)}
								>
									<Box as="span" xcss={styles.themeInfoButtonWrapper}>
										<IconButton
											appearance="subtle"
											spacing="compact"
											icon={InformationCircleIcon}
											label={formatMessage(messages.themeInfoButtonLabel, { themeName })}
										/>
									</Box>
								</Tooltip>
							</Inline>
						</Inline>
						<ChevronIcon label="" color={token('color.icon')} size="small" />
					</Inline>
				</Box>
			</Box>
			{isExpanded && <ThemeDetails theme={theme} customerProfile={customerProfile} intl={intl} />}
		</Box>
	);
};

const ThemeSection = ({
	heading,
	description,
	themes,
	expandedThemes,
	onToggleTheme,
	customerProfile,
	intl,
}: {
	heading: string;
	description?: string;
	themes: readonly FastshiftCsProfilerCustomerProfileTheme[];
	expandedThemes: Record<string, boolean>;
	onToggleTheme: (theme: FastshiftCsProfilerCustomerProfileTheme) => void;
	customerProfile: FastshiftCsProfilerCustomerProfile;
	intl: IntlShape;
}) => {
	if (themes.length === 0) {
		return null;
	}

	return (
		<Stack space="space.150">
			<Stack space="space.050">
				<Heading size="xsmall" as="h3">
					{heading}
				</Heading>
				{description && <Text color="color.text.subtle">{description}</Text>}
			</Stack>
			<Stack space="space.100">
				{themes.map((theme) => {
					const themeName = theme.themeDetails.name;

					return (
						<ThemeRow
							key={themeName}
							theme={theme}
							isExpanded={Boolean(expandedThemes[themeName])}
							onToggle={() => onToggleTheme(theme)}
							customerProfile={customerProfile}
							intl={intl}
						/>
					);
				})}
			</Stack>
		</Stack>
	);
};

export const FastshiftCsProfilerThemeDisplay = ({
	customerProfile,
	isExportingToConfluence = false,
	onExportToConfluence,
}: FastshiftCsProfilerThemeDisplayProps) => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const [expandedThemes, setExpandedThemes] = useState<Record<string, boolean>>({});
	const { sendThemeRowToggleClickUIEvent } = useFastshiftCsProfilerAnalytics();
	const blockerThemes = sortThemesBySeverity(
		customerProfile.themes.filter(({ tier }) => tier !== 'WATCH'),
	);
	const watchThemes = customerProfile.themes.filter(({ tier }) => tier === 'WATCH');

	useEffect(() => {
		setExpandedThemes({});
	}, [customerProfile]);

	const handleToggleTheme = useCallback(
		(theme: FastshiftCsProfilerCustomerProfileTheme) => {
			const themeName = theme.themeDetails.name;
			const isExpanded = !expandedThemes[themeName];

			sendThemeRowToggleClickUIEvent({
				themeName,
				themeTier: theme.tier,
				isExpanded,
			});

			setExpandedThemes({
				...expandedThemes,
				[themeName]: isExpanded,
			});
		},
		[expandedThemes, sendThemeRowToggleClickUIEvent],
	);

	return (
		<Box
			as="section"
			xcss={styles.resultsPanel}
			aria-label={formatMessage(messages.resultsHeading)}
		>
			<Stack space="space.250">
				<Stack space="space.075">
					<Stack space="space.050">
						<Box xcss={styles.resultsHeadingRow}>
							<Inline alignBlock="end" spread="space-between" space="space.150">
								<Heading size="small" as="h2">
									{formatMessage(messages.resultsHeading)}
								</Heading>
								<Box xcss={styles.exportButtonWrapper}>
									<Button
										spacing="compact"
										isLoading={isExportingToConfluence}
										onClick={onExportToConfluence}
									>
										{formatMessage(messages.exportToConfluenceButton)}
									</Button>
								</Box>
							</Inline>
						</Box>
						<hr
							role="presentation"
							css={styles.resultsHeadingDivider}
							aria-hidden="true"
							data-testid="cs-profiler-results-heading-divider"
						/>
					</Stack>
					<Stack alignInline="start" space="space.075">
						<Text color="color.text.subtle">
							{formatMessage(messages.resultSummary, {
								totalItems: customerProfile.totalItems,
								blockerCount: customerProfile.blockerCount,
								watchCount: customerProfile.watchCount,
							})}
						</Text>
						<Box as="span" xcss={styles.resultProfileChip}>
							<Text size="small" weight="semibold" color="color.text.subtle">
								{formatMessage(messages.selectedProfileSummary, {
									region: customerProfile.selection.region,
									industry: customerProfile.selection.industry,
									segment: customerProfile.selection.segment,
								})}
							</Text>
						</Box>
					</Stack>
				</Stack>

				{customerProfile.themes.length > 0 ? (
					<Box xcss={styles.themeSectionsWrapper}>
						<Stack space="space.300">
							<ThemeSection
								heading={formatMessage(messages.blockerSectionHeading)}
								description={formatMessage(messages.blockerSectionDescription)}
								themes={blockerThemes}
								expandedThemes={expandedThemes}
								onToggleTheme={handleToggleTheme}
								customerProfile={customerProfile}
								intl={intl}
							/>
							<ThemeSection
								heading={formatMessage(messages.watchSectionHeading)}
								description={formatMessage(messages.watchSectionDescription)}
								themes={watchThemes}
								expandedThemes={expandedThemes}
								onToggleTheme={handleToggleTheme}
								customerProfile={customerProfile}
								intl={intl}
							/>
						</Stack>
					</Box>
				) : (
					<Text color="color.text.subtle">{formatMessage(messages.noThemes)}</Text>
				)}
			</Stack>
		</Box>
	);
};
